<script setup lang="ts">
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import type { FastCheckoutMethod } from '@design-system/composables/useFastCheckoutMethods'

/**
 * Fast checkout entry point on the PDP, rendered right below the add to cart button.
 *
 * The layout depends on how many fast payment methods the shopper can actually use:
 * - 1 method -> full-width wallet CTA
 * - 2 methods -> the two CTAs side by side
 * - 3+        -> a "Fast checkout" row with reduced logos
 *
 * Without a size, a click opens the size selector with the matching footer CTA.
 * With a size already selected, 1–2 methods start payment on the SDK button and
 * 3+ open the method-chooser sidesheet.
 *
 * The shopper always sees the same method buttons: once a size is picked the
 * SDK buttons are mounted transparently on top of them (see
 * `PdpEcommerceBlockProductFastCheckoutWallet`), once per express order.
 * Picking another size only swaps the order line item. PayPal takes the click
 * as soon as a size is picked; Apple Pay and Google Pay are shown disabled
 * until their SDK buttons do (first size, or a size swap in progress).
 */
const { addToCartPayload } = useAddToCart()
const { ensureOrder, discard } = useExpressOrder()
const { openDialog } = useDialog()
const { availableMethods, methodsCount, isResolved } = useFastCheckoutMethods()
const {
  isPreparing,
  preparedOrderId,
  isWalletReady,
  isPdpFastCheckoutHidden,
  setMethodIntent,
  setChooserIntent,
} = useFastCheckoutIntent()

const { preload } = useFastCheckoutPreload()

watch(
  availableMethods,
  methods => {
    if (methods?.length && !isPdpFastCheckoutHidden.value) preload(methods)
  },
  { immediate: true }
)

const hasSelectedSku = computed(() => !!addToCartPayload.value?.sku_code)
const isRowBusy = computed(() => hasSelectedSku.value && isPreparing.value)

/** PayPal stays clickable: its `createOrder` waits for the order being prepared. */
const isMethodBusy = (method: FastCheckoutMethod) =>
  isRowBusy.value && method !== 'payPal'

watch(
  () => addToCartPayload.value?.sku_code,
  async sku => {
    if (import.meta.server) return
    if (isPdpFastCheckoutHidden.value || !sku || !addToCartPayload.value) return
    isPreparing.value = true
    try {
      const expressOrder = await ensureOrder({
        sku_code: addToCartPayload.value.sku_code,
        name: addToCartPayload.value.name,
        metadata: addToCartPayload.value.metadata,
      })
      preparedOrderId.value = expressOrder?.id ?? null
    } catch (error) {
      console.error('Failed to prepare PDP express order', error)
    } finally {
      isPreparing.value = false
    }
  },
  { immediate: true }
)

onBeforeRouteLeave(to => {
  if (to.path.includes('/review')) return
  void discard()
})

/**
 * With a size picked the SDK button on top takes the click, and the method
 * button stays disabled until it does.
 */
const handleMethodClick = (method: FastCheckoutMethod) => {
  if (hasSelectedSku.value) return
  setMethodIntent(method)
  openDialog(SIDEBAR_IDS.pdpEcommerceBlockSizeSelector)
}

const handleOpenSidesheet = () => {
  if (!hasSelectedSku.value) {
    setChooserIntent()
    openDialog(SIDEBAR_IDS.pdpEcommerceBlockSizeSelector)
    return
  }
  openDialog(SIDEBAR_IDS.pdpEcommerceBlockFastCheckout)
}

const walletContainerId = (method: FastCheckoutMethod) =>
  `pdp-${method}-container`
</script>

<template>
  <ClientOnly>
    <!--
      Height is reserved while availability is still resolving so the content
      below the add to cart button never shifts.
    -->
    <div v-if="isPdpFastCheckoutHidden" />
    <div v-else-if="!isResolved" class="mt-2 h-[52px]" aria-hidden="true" />

    <div v-else-if="methodsCount" class="mt-2" data-testid="pdp-fast-checkout">
      <div v-if="methodsCount <= 2" class="h-[52px]">
        <div class="flex gap-2" :aria-busy="isRowBusy || undefined">
          <div
            v-for="method in availableMethods ?? []"
            :key="method"
            class="min-w-0 flex-1"
          >
            <PdpEcommerceBlockProductFastCheckoutWallet
              :method="method"
              surface="pdp"
              :compact="methodsCount === 2"
              :is-sdk-ready="isWalletReady"
              :is-awaiting-sdk="hasSelectedSku"
              :disabled="isMethodBusy(method)"
              :container-id="walletContainerId(method)"
              @click="handleMethodClick(method)"
            />
          </div>
        </div>
      </div>

      <PdpEcommerceBlockProductFastCheckoutLogosButton
        v-else
        :methods="availableMethods ?? []"
        @click="handleOpenSidesheet"
      />
    </div>
  </ClientOnly>
</template>
